import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BACKEND_URL, getToken } from '../auth/api';
import {
  DEFAULT_RACE_RULES,
  EMPTY_RACE_PLAN,
  RaceControlEntry,
  RacePlan,
  RaceRules,
  TeamData,
} from '../types';
import { TeamInfo } from '../TeamInfo';
import { applyTeamData, teamDataToTeamInfo } from '../teamState';
import {
  AppendedLap,
  appendLap,
  emptyLapHistory,
  LapHistory,
  seedFromStints,
} from '../analytics/lapHistory';
import { createFrameSharer, createFrameThinner, shouldThin } from '../analytics/replayThinning';
import {
  emptySectorHistory,
  recordSectors,
  SectorHistory,
} from '../analytics/sectorHistory';

/** Kinds the replay reproduces; everything else in the log is ignored. */
const REPLAYABLE = new Set([
  'teamUpdate',
  'raceControl',
  'sessionTimer',
  'raceRules',
  'racePlan',
  'teamLapAppended',
]);

/**
 * Safety valve for a log that would not fit in a tab. A full 13-hour race is 1.14
 * million entries and takes about 0.8 GB once repeated data is shared, so this is
 * well above any real recording; it is a fence, not a working limit.
 */
const MAX_ENTRIES = 3_000_000;

/** How often the virtual clock advances and the grid re-renders. */
const TICK_MS = 100;

export const REPLAY_SPEEDS = [1, 2, 5, 10, 30, 60] as const;

interface ReplayEntry {
  t: number;
  kind: string;
  data: unknown;
}

export type ReplayStatus = 'loading' | 'ready' | 'empty' | 'error';

export interface UseReplayResult {
  status: ReplayStatus;
  error: string | null;
  /** Bytes pulled so far, for the loading progress readout. */
  loadedBytes: number;
  entryCount: number;
  truncated: boolean;

  isPlaying: boolean;
  speed: number;
  setSpeed: (speed: number) => void;
  togglePlay: () => void;
  seekTo: (time: number) => void;
  restart: () => void;

  /** Timeline bounds and position, in original wall-clock epoch ms. */
  startsAt: number;
  endsAt: number;
  currentTime: number;

  teams: Map<string, TeamInfo>;
  raceControl: RaceControlEntry[];
  sessionEndsAt: number | null;
  /** Rules as they stood at the current replay position. */
  raceRules: RaceRules;
  /** The running order as it stood at the current position. */
  racePlan: RacePlan;
  /** True when frames were dropped to suit a device with little memory. */
  light: boolean;
  /** Lap history up to the current position. */
  lapHistory: LapHistory;
  lapHistoryVersion: number;
  /** Sector times per lap up to the playhead. */
  sectorHistory: SectorHistory;
  /** Bumped on every settle, like `lapHistoryVersion`. */
  sectorHistoryVersion: number;
}

/**
 * Plays a recorded event back from its raw JSONL log.
 *
 * The virtual clock is the original epoch time, which means stored absolute
 * values — notably the session countdown's `endsAt` — stay meaningful without
 * any rebasing.
 *
 * Every `teamUpdate` is a complete snapshot of one team rather than a delta, so
 * seeking forward only needs to apply the entries in between; seeking backwards
 * rebuilds from the start, which is fast enough at these entry counts.
 */
export const useReplay = (eventId: string): UseReplayResult => {
  const [status, setStatus] = useState<ReplayStatus>('loading');
  const [error, setError] = useState<string | null>(null);
  const [loadedBytes, setLoadedBytes] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [isLight, setIsLight] = useState(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState<number>(10);
  const [currentTime, setCurrentTime] = useState(0);

  const [teams, setTeams] = useState<Map<string, TeamInfo>>(new Map());
  const [raceControl, setRaceControl] = useState<RaceControlEntry[]>([]);
  const [sessionEndsAt, setSessionEndsAt] = useState<number | null>(null);
  const [raceRules, setRaceRules] = useState<RaceRules>(DEFAULT_RACE_RULES);
  const [lapHistoryVersion, setLapHistoryVersion] = useState(0);
  const [racePlan, setRacePlan] = useState<RacePlan>(EMPTY_RACE_PLAN);

  const entriesRef = useRef<ReplayEntry[]>([]);
  const cursorRef = useRef(0);
  const teamsRef = useRef<Map<string, TeamInfo>>(new Map());
  const raceControlRef = useRef<RaceControlEntry[]>([]);
  const sessionEndsAtRef = useRef<number | null>(null);
  const raceRulesRef = useRef<RaceRules>(DEFAULT_RACE_RULES);
  const lapHistoryRef = useRef<LapHistory>(emptyLapHistory());
  // Unlike laps, sectors are not truncated against the stint rows when read, so a
  // backwards seek starts this store over rather than trying to unwind it.
  const sectorHistoryRef = useRef<SectorHistory>(emptySectorHistory());
  const racePlanRef = useRef<RacePlan>(EMPTY_RACE_PLAN);
  const clockRef = useRef(0);

  const [bounds, setBounds] = useState<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });

  // ── Load ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      setStatus('loading');
      setError(null);
      try {
        const token = getToken();
        const res = await fetch(
          `${BACKEND_URL}/events/${eventId}/raw/download`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
        );
        if (!res.ok) {
          throw new Error(
            res.status === 404
              ? 'This event has no raw log to replay'
              : `Could not load the log (HTTP ${res.status})`,
          );
        }
        if (!res.body) throw new Error('Streaming is not supported here');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        const entries: ReplayEntry[] = [];
        let buffer = '';
        let bytes = 0;
        let hitCap = false;
        // Every frame is kept, with repeated tables shared between them. A device that
        // reports little memory gets the thinned replay instead.
        const sharer = createFrameSharer();
        const light = shouldThin(
          (navigator as Navigator & { deviceMemory?: number }).deviceMemory,
        );
        const thinner = light ? createFrameThinner() : null;
        setIsLight(light);

        const takeLine = (line: string): void => {
          if (!line) return;
          try {
            const parsed = JSON.parse(line) as Partial<ReplayEntry>;
            if (
              typeof parsed.t === 'number' &&
              typeof parsed.kind === 'string' &&
              REPLAYABLE.has(parsed.kind)
            ) {
              const entry = { t: parsed.t, kind: parsed.kind, data: parsed.data };
              if (!thinner || thinner.keep(entry)) {
                sharer.share(entry);
                entries.push(entry);
              }
            }
          } catch {
            // A truncated final line in a still-open log; skip it.
          }
        };

        for (;;) {
          const { done, value } = await reader.read();
          if (cancelled) {
            void reader.cancel();
            return;
          }
          if (done) break;

          bytes += value.byteLength;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          for (const line of lines) takeLine(line);

          setLoadedBytes(bytes);

          if (entries.length >= MAX_ENTRIES) {
            hitCap = true;
            void reader.cancel();
            break;
          }
        }
        if (!hitCap) takeLine(buffer);
        if (cancelled) return;

        if (!hitCap && thinner) {
          // The last frame of each team, so the finishing order is exact. They are
          // the newest of their team but not of the log, hence the sort.
          const closing = thinner.flush();
          if (closing.length > 0) {
            closing.forEach((entry) => sharer.share(entry));
            entries.push(...closing);
            entries.sort((a, b) => a.t - b.t);
          }
        }

        if (entries.length === 0) {
          setStatus('empty');
          return;
        }

        entriesRef.current = entries;
        setTruncated(hitCap);
        const start = entries[0].t;
        const end = entries[entries.length - 1].t;
        setBounds({ start, end });
        clockRef.current = start;
        setCurrentTime(start);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load the log');
        setStatus('error');
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  // ── State reconstruction ────────────────────────────────────────────────────
  const applyEntry = useCallback((entry: ReplayEntry): void => {
    if (entry.kind === 'teamLapAppended') {
      appendLap(lapHistoryRef.current, entry.data as AppendedLap);
      return;
    }
    if (entry.kind === 'teamUpdate') {
      const data = entry.data as TeamData;
      if (!data?.id) return;

      // Recordings made before laps moved onto their own event carry them here.
      seedFromStints(lapHistoryRef.current, data.id, data.stintAverages ?? []);
      recordSectors(sectorHistoryRef.current, data, entry.t);

      const existing = teamsRef.current.get(data.id);
      if (existing) {
        applyTeamData(existing, data);
      } else {
        teamsRef.current.set(data.id, teamDataToTeamInfo(data));
      }
      return;
    }
    if (entry.kind === 'raceControl') {
      raceControlRef.current = Array.isArray(entry.data)
        ? (entry.data as RaceControlEntry[])
        : [];
      return;
    }
    if (entry.kind === 'sessionTimer') {
      const timer = entry.data as { endsAt?: number } | null;
      sessionEndsAtRef.current =
        typeof timer?.endsAt === 'number' ? timer.endsAt : null;
      return;
    }
    if (entry.kind === 'racePlan') {
      racePlanRef.current = (entry.data as RacePlan | null) ?? EMPTY_RACE_PLAN;
      return;
    }
    if (entry.kind === 'raceRules') {
      // Events recorded before rules existed simply never emit this, and the
      // replay keeps the defaults.
      raceRulesRef.current = {
        ...DEFAULT_RACE_RULES,
        ...((entry.data as Partial<RaceRules> | null) ?? {}),
      };
    }
  }, []);

  /** Advances (or rebuilds) internal state so it reflects exactly `target`. */
  const settleAt = useCallback(
    (target: number): void => {
      const entries = entriesRef.current;

      if (target < clockRef.current) {
        teamsRef.current = new Map();
        raceControlRef.current = [];
        sessionEndsAtRef.current = null;
        raceRulesRef.current = DEFAULT_RACE_RULES;
        racePlanRef.current = EMPTY_RACE_PLAN;
        sectorHistoryRef.current = emptySectorHistory();
        cursorRef.current = 0;
      }

      while (
        cursorRef.current < entries.length &&
        entries[cursorRef.current].t <= target
      ) {
        applyEntry(entries[cursorRef.current]);
        cursorRef.current += 1;
      }

      clockRef.current = target;
      setCurrentTime(target);
      setTeams(new Map(teamsRef.current));
      setRaceControl(raceControlRef.current);
      setSessionEndsAt(sessionEndsAtRef.current);
      setRaceRules(raceRulesRef.current);
      setRacePlan(racePlanRef.current);
      setLapHistoryVersion((v) => v + 1);
    },
    [applyEntry],
  );

  // ── Playback ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isPlaying || status !== 'ready') return;

    let last = Date.now();
    const id = setInterval(() => {
      const now = Date.now();
      const advanced = (now - last) * speed;
      last = now;

      const next = clockRef.current + advanced;
      if (next >= bounds.end) {
        settleAt(bounds.end);
        setIsPlaying(false);
        return;
      }
      settleAt(next);
    }, TICK_MS);

    return () => clearInterval(id);
  }, [isPlaying, speed, status, bounds.end, settleAt]);

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => {
      // Starting again from the very end should replay rather than sit still.
      if (!prev && clockRef.current >= bounds.end) settleAt(bounds.start);
      return !prev;
    });
  }, [bounds.start, bounds.end, settleAt]);

  const seekTo = useCallback(
    (time: number) => {
      const clamped = Math.min(Math.max(time, bounds.start), bounds.end);
      settleAt(clamped);
    },
    [bounds.start, bounds.end, settleAt],
  );

  const restart = useCallback(() => {
    settleAt(bounds.start);
  }, [bounds.start, settleAt]);

  return useMemo(
    () => ({
      status,
      error,
      loadedBytes,
      entryCount: entriesRef.current.length,
      truncated,
      light: isLight,
      isPlaying,
      speed,
      setSpeed,
      togglePlay,
      seekTo,
      restart,
      startsAt: bounds.start,
      endsAt: bounds.end,
      currentTime,
      teams,
      raceControl,
      sessionEndsAt,
      raceRules,
      racePlan,
      lapHistory: lapHistoryRef.current,
      lapHistoryVersion,
      sectorHistory: sectorHistoryRef.current,
      sectorHistoryVersion: lapHistoryVersion,
    }),
    [
      status,
      error,
      loadedBytes,
      truncated,
      isLight,
      isPlaying,
      speed,
      togglePlay,
      seekTo,
      restart,
      bounds.start,
      bounds.end,
      currentTime,
      teams,
      raceControl,
      sessionEndsAt,
      raceRules,
      racePlan,
      lapHistoryVersion,
    ],
  );
};
